// material-ui
import { useEffect, useRef, useState } from 'react';
import { Theme } from '@mui/material/styles';
import { Box, useMediaQuery, Grid, Typography } from '@mui/material';
import GlobalSearch from './Search/GlobalSearch';
import { useEnvironment } from 'hooks/use-environment';

// project import
import Profile from './Profile';
import Notification from './Notification';
import { MuteButton } from './MuteButton';
import globalSearchContext from './Search/use-global-search';

// ==============================|| HEADER - CONTENT ||============================== //

const HeaderContent = () => {
  const { isProd } = useEnvironment();
  const matchesXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(blurTimeoutRef.current), []);

  // On mobile, expand the search to take over the row while it has focus (hiding the toggle
  // and mute/bell icons, which don't have room alongside a usable search field) and shrink it
  // back once focus leaves. Desktop is unaffected - there's already room for everything.
  const isMobileSearchExpanded = matchesXs && isSearchFocused;

  const handleSearchFocus = () => {
    clearTimeout(blurTimeoutRef.current);
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    // Delay collapsing briefly: the autocomplete dropdown renders in a portal outside this
    // element, so a click on an option blurs the input before the selection is registered.
    blurTimeoutRef.current = setTimeout(() => setIsSearchFocused(false), 150);
  };

  const testEnvironmentBanner = (
    <Typography flexWrap={'wrap'} textAlign={'center'} fontWeight="bold" variant={matchesXs ? 'body2' : 'h3'}>
      *** TEST ENVIRONMENT ***
    </Typography>
  );

  return (
    <Grid container alignItems="center">
      <Grid container item xs={12} wrap="nowrap" alignItems="center" justifyContent="space-between">
        <Grid
          item
          marginLeft={{ xs: 1, md: 6 }}
          sx={{ minWidth: 0, flexBasis: { xs: 0, md: 'auto' }, flexGrow: { xs: 1, md: 0 }, overflow: 'hidden' }}
        >
          <globalSearchContext.Provider>
            <GlobalSearch hideToggle={isMobileSearchExpanded} onSearchFocus={handleSearchFocus} onSearchBlur={handleSearchBlur} />
          </globalSearchContext.Provider>
        </Grid>
        {!isProd && !matchesXs && (
          <Grid item alignSelf={'center'}>
            {testEnvironmentBanner}
          </Grid>
        )}
        {!isMobileSearchExpanded && (
          <Grid item sx={{ flexShrink: 0 }}>
            <Grid container direction="row" alignItems="center" spacing={2} wrap="nowrap">
              {matchesXs && (
                <Grid item>
                  <Box sx={{ width: '100%', ml: 1 }} />
                </Grid>
              )}
              <Grid item>
                <MuteButton />
              </Grid>
              <Grid item>
                <Notification />
              </Grid>
              {!matchesXs && (
                <Grid item>
                  <Profile />
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
      {!isProd && matchesXs && (
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          {testEnvironmentBanner}
        </Grid>
      )}
    </Grid>
  );
};

export default HeaderContent;
